import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, where, doc, deleteDoc } from 'firebase/firestore'
import { db } from '../config/firebase'
import { projectsAPI, trainingAPI, courseProgressAPI, workerPortalAPI } from '../services/api'
import ProjectManagementCoursePro from './ProjectManagementCoursePro'
import LogisticsCoursePro from './LogisticsCoursePro'
import EnglishCoursePro from './EnglishCoursePro'
import LeadershipCoursePro from './LeadershipCoursePro'
import WorkerBilling from './WorkerBilling'
import './WorkerDashboard.css'
import './AdminDashboard.sales.css'

const SESSION_KEY = 'worker_session'
const LANG_KEY    = 'dashboard_lang'

const MENU_SECTIONS = [
  {
    key: 'schedule', icon: '📅',
    label: { en: 'Schedule', fr: 'Calendrier' },
    items: [
      { en: 'My Schedule',       fr: 'Mon calendrier',               tab: 'my-schedule' },
      { en: 'Shift Calendar',    fr: 'Calendrier des quarts',         tab: 'shift-calendar' },
      { en: 'Time Off Requests', fr: 'Demandes de congé',             tab: 'time-off' },
      { en: 'Overtime',          fr: 'Heures supplémentaires',        tab: 'overtime' },
    ],
  },
  {
    key: 'workplace', icon: '🏢',
    label: { en: 'Workplace', fr: 'Lieu de travail' },
    items: [
      { en: 'Site Map',          fr: 'Plan du site',                  tab: 'site-map' },
      { en: 'Facilities',        fr: 'Installations',                 tab: 'facilities' },
      { en: 'Equipment Request', fr: 'Demande de matériel',           tab: 'equipment-request' },
      { en: 'IT Support',        fr: 'Support informatique',          tab: 'it-support' },
    ],
  },
  {
    key: 'benefits', icon: '💰',
    label: { en: 'Benefits & Pay', fr: 'Avantages & Paie' },
    items: [
      { en: 'Pay Stubs',         fr: 'Bulletins de paie',             tab: 'pay-stubs' },
      { en: 'Health Insurance',  fr: 'Assurance santé',               tab: 'health-insurance' },
      { en: 'Retirement Plan',   fr: 'Plan de retraite',              tab: 'retirement' },
      { en: 'Expense Claims',    fr: 'Notes de frais',                tab: 'expenses' },
    ],
  },
  {
    key: 'community', icon: '🤝',
    label: { en: 'Community', fr: 'Communauté' },
    items: [
      { en: 'Announcements',     fr: 'Annonces',                      tab: 'announcements' },
      { en: 'Team Directory',    fr: "Annuaire de l'équipe",           tab: 'team-directory' },
      { en: 'Events',            fr: 'Événements',                    tab: 'events' },
      { en: 'Recognition',       fr: 'Reconnaissance',                tab: 'recognition' },
    ],
  },
  {
    key: 'career', icon: '🚀',
    label: { en: 'Career', fr: 'Carrière' },
    items: [
      { en: 'My Goals',          fr: 'Mes objectifs',                  tab: 'my-goals' },
      { en: 'Performance Review',fr: 'Évaluation de performance',      tab: 'performance' },
      { en: 'Internal Jobs',     fr: 'Offres internes',                tab: 'internal-jobs' },
      { en: 'Mentorship',        fr: 'Mentorat',                       tab: 'mentorship' },
    ],
  },
  {
    key: 'learn', icon: '📚',
    label: { en: 'Learn', fr: 'Apprendre' },
    items: [
      { en: 'My Training',       fr: 'Ma Formation',                   tab: 'training' },
      { en: 'Course Library',    fr: 'Bibliothèque de cours',          tab: 'course-library' },
      { en: 'Certifications',    fr: 'Certifications',                 tab: 'certifications' },
      { en: 'Workshops',         fr: 'Ateliers',                       tab: 'workshops' },
    ],
  },
  {
    key: 'safety', icon: '🛡️',
    label: { en: 'Security, Safety & Ethics', fr: 'Sécurité & Éthique' },
    items: [
      { en: 'Safety Guidelines', fr: 'Consignes de sécurité',         tab: 'safety-guidelines' },
      { en: 'Incident Report',   fr: "Rapport d'incident",            tab: 'incident-report' },
      { en: 'Code of Conduct',   fr: 'Code de conduite',              tab: 'code-of-conduct' },
      { en: 'Whistleblower',     fr: 'Alerte éthique',                tab: 'whistleblower' },
    ],
  },
]

/* ── Placeholder page data ──────────────────────────────────────── */
const PLACEHOLDER_PAGES = {
  'overtime':           { icon: '⏰', en: 'Overtime',           fr: 'Heures Supp.',        desc_en: 'View and manage your overtime hours. Requests must be approved by your manager.',                   desc_fr: 'Consultez et gérez vos heures supplémentaires.' },
  'site-map':           { icon: '🗺️', en: 'Site Map',          fr: 'Plan du Site',        desc_en: 'Interactive site map showing all facilities, buildings, and restricted zones.',                      desc_fr: 'Carte interactive du site avec les bâtiments et zones.' },
  'facilities':         { icon: '🏢', en: 'Facilities',         fr: 'Installations',       desc_en: 'Find information about all company facilities, meeting rooms, and amenities.',                      desc_fr: 'Informations sur les installations et salles de réunion.' },
  'equipment-request':  { icon: '🔧', en: 'Equipment Request',  fr: 'Demande Matériel',    desc_en: 'Submit requests for tools, PPE, devices, or other work equipment.',                                desc_fr: 'Soumettez des demandes d\'équipement ou de matériel.' },
  'pay-stubs':          { icon: '💵', en: 'Pay Stubs',          fr: 'Bulletins de Paie',   desc_en: 'View and download your monthly pay stubs and salary history.',                                     desc_fr: 'Consultez et téléchargez vos bulletins de paie.' },
  'health-insurance':   { icon: '🏥', en: 'Health Insurance',   fr: 'Assurance Santé',     desc_en: 'Your health insurance coverage details, claims, and provider directory.',                          desc_fr: 'Couverture santé, remboursements et réseau de soins.' },
  'retirement':         { icon: '🎯', en: 'Retirement Plan',    fr: 'Plan de Retraite',    desc_en: 'View your retirement savings, contributions, and projected benefits.',                              desc_fr: 'Consultez votre épargne retraite et contributions.' },
  'expenses':           { icon: '🧾', en: 'Expense Claims',     fr: 'Notes de Frais',      desc_en: 'Submit and track work-related expense reimbursements.',                                            desc_fr: 'Soumettez et suivez vos notes de frais professionnels.' },
  'announcements':      { icon: '📢', en: 'Announcements',      fr: 'Annonces',            desc_en: 'Company-wide announcements, news, and updates from management.',                                   desc_fr: 'Annonces de la direction et actualités de l\'entreprise.' },
  'events':             { icon: '🎉', en: 'Events',             fr: 'Événements',          desc_en: 'Upcoming company events, training days, team gatherings, and public holidays.',                     desc_fr: 'Événements, journées de formation et jours fériés.' },
  'recognition':        { icon: '🏆', en: 'Recognition',        fr: 'Reconnaissance',      desc_en: 'Recognize and celebrate colleague achievements and milestones.',                                   desc_fr: 'Valorisez et célébrez les succès de vos collègues.' },
  'my-goals':           { icon: '🎯', en: 'My Goals',           fr: 'Mes Objectifs',       desc_en: 'Set, track, and review your personal and professional performance goals.',                         desc_fr: 'Définissez et suivez vos objectifs professionnels.' },
  'performance':        { icon: '📈', en: 'Performance Review', fr: 'Évaluation Perf.',    desc_en: 'Access your performance review history and upcoming evaluations.',                                 desc_fr: 'Consultez votre historique d\'évaluation de performance.' },
  'internal-jobs':      { icon: '💼', en: 'Internal Jobs',      fr: 'Offres Internes',     desc_en: 'Browse open positions within Groupe Ouambo and apply for internal transfers.',                    desc_fr: 'Parcourez les postes ouverts au sein du Groupe Ouambo.' },
  'mentorship':         { icon: '🤝', en: 'Mentorship',         fr: 'Mentorat',            desc_en: 'Connect with mentors or become one. Grow your career with guidance.',                             desc_fr: 'Connectez-vous avec des mentors ou devenez mentor.' },
  'course-library':     { icon: '📖', en: 'Course Library',     fr: 'Bibliothèque Cours',  desc_en: 'Browse all available training courses, learning paths, and resources.',                           desc_fr: 'Parcourez tous les cours et ressources disponibles.' },
  'certifications':     { icon: '🎓', en: 'Certifications',     fr: 'Certifications',      desc_en: 'View your earned certificates and track certification requirements.',                              desc_fr: 'Consultez vos certificats obtenus et en cours.' },
  'workshops':          { icon: '🔬', en: 'Workshops',          fr: 'Ateliers',            desc_en: 'Register for hands-on workshops and practical skills training sessions.',                         desc_fr: 'Inscrivez-vous aux ateliers pratiques et formations.' },
  'incident-report':    { icon: '⚠️', en: 'Incident Report',    fr: "Rapport d'Incident",  desc_en: 'Report workplace incidents, near-misses, or safety concerns confidentially.',                    desc_fr: 'Signalez les incidents, quasi-accidents ou risques.' },
  'whistleblower':      { icon: '🔔', en: 'Whistleblower',      fr: 'Alerte Éthique',      desc_en: 'Report ethical violations anonymously. All reports are handled confidentially.',                  desc_fr: 'Signalez des violations éthiques de façon confidentielle.' },
}

const TAB_TITLES = {
  en: {
    home: 'Home', projects: 'My Projects', training: 'My Training',
    feed: 'Activity Feed', profile: 'My Profile',
    'it-support': 'IT Support', 'my-schedule': 'My Schedule',
    'shift-calendar': 'Shift Calendar', 'time-off': 'Time Off',
    'overtime': 'Overtime', 'site-map': 'Site Map',
    'facilities': 'Facilities', 'equipment-request': 'Equipment Request',
    'pay-stubs': 'Pay Stubs', 'health-insurance': 'Health Insurance',
    'retirement': 'Retirement Plan', 'expenses': 'Expense Claims',
    'announcements': 'Announcements', 'team-directory': 'Team Directory',
    'events': 'Events', 'recognition': 'Recognition',
    'my-goals': 'My Goals', 'performance': 'Performance Review',
    'internal-jobs': 'Internal Jobs', 'mentorship': 'Mentorship',
    'course-library': 'Course Library', 'certifications': 'Certifications',
    'workshops': 'Workshops', 'safety-guidelines': 'Safety Guidelines',
    'incident-report': 'Incident Report', 'code-of-conduct': 'Code of Conduct',
    'whistleblower': 'Whistleblower',
    quotes: 'Quotes',
    invoices: 'Invoices',
  },
  fr: {
    home: 'Accueil', projects: 'Mes Projets', training: 'Ma Formation',
    feed: "Fil d'Activité", profile: 'Mon Profil',
    'it-support': 'Support Informatique', 'my-schedule': 'Mon Calendrier',
    'shift-calendar': 'Calendrier des Quarts', 'time-off': 'Demandes de Congé',
    'overtime': 'Heures Supp.', 'site-map': 'Plan du Site',
    'facilities': 'Installations', 'equipment-request': 'Demande Matériel',
    'pay-stubs': 'Bulletins de Paie', 'health-insurance': 'Assurance Santé',
    'retirement': 'Plan de Retraite', 'expenses': 'Notes de Frais',
    'announcements': 'Annonces', 'team-directory': "Annuaire d'Équipe",
    'events': 'Événements', 'recognition': 'Reconnaissance',
    'my-goals': 'Mes Objectifs', 'performance': 'Évaluation Perf.',
    'internal-jobs': 'Offres Internes', 'mentorship': 'Mentorat',
    'course-library': 'Bibliothèque Cours', 'certifications': 'Certifications',
    'workshops': 'Ateliers', 'safety-guidelines': 'Consignes Sécurité',
    'incident-report': "Rapport d'Incident", 'code-of-conduct': 'Code de Conduite',
    'whistleblower': 'Alerte Éthique',
    quotes: 'Devis',
    invoices: 'Factures',
  }
}

const translations = {
  en: {
    dashboardTitle: 'Worker Dashboard',
    portalSub: 'Groupe Ouambo — Staff Portal',
    fullName: 'Full Name',
    selectName: 'Select your name',
    password: 'Password',
    yourPassword: 'Your password',
    signIn: 'Sign In',
    backToWebsite: '← Back to website',
    signOut: 'Sign Out',
    website: '← Website',
    myProjects: 'My Projects',
    training: 'Training',
    activityFeed: 'Activity Feed',
    profile: 'Profile',
    projectDetail: 'Project Detail',
    backToProjects: '← Back to Projects',
    assigned: 'Assigned',
    active: 'Active',
    done: 'Done',
    comments: 'Comments',
    noProjectsAssigned: 'No projects assigned yet. Contact your admin.',
    progress: 'Progress',
    completion: 'Completion',
    logProgressUpdate: 'Log Progress Update',
    note: 'Note',
    status: 'Status',
    keepCurrent: 'Keep current',
    submitUpdate: 'Submit Update',
    updateHistory: 'Update History',
    noUpdatesYet: 'No updates yet.',
    discussion: 'Discussion',
    noCommentsYet: 'No comments yet.',
    writeAComment: 'Write a comment...',
    postComment: 'Post Comment',
    myTraining: 'My Training',
    coursesAssigned: 'Courses assigned to you by the admin. Click to start or continue.',
    inProgress: 'In Progress',
    completed: 'Completed',
    noCoursesAssigned: 'No courses assigned yet. Contact your administrator.',
    startCourse: 'Start Course',
    continueCourse: 'Continue Course',
    reviewCourse: 'Review Course',
    badgeEarned: 'Badge Earned',
    earnedBadges: 'Earned Badges',
    activityFeedTitle: 'Activity Feed',
    recentActivity: 'Recent updates and comments on your projects.',
    noActivityYet: 'No activity yet. Start working on your projects.',
    progressUpdate: 'Progress Update',
    comment: 'Comment',
    myProfile: 'My Profile',
    updateProfileDesc: 'Update your profile picture and password.',
    changePhoto: 'Change Photo',
    changePassword: 'Change Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    enterNewPassword: 'Enter new password',
    reenterNewPassword: 'Re-enter new password',
    updatePassword: 'Update Password',
    home: 'Home',
    welcomeBack: 'Welcome back',
    quickStats: 'Quick Overview',
    quickLinks: 'Quick Links',
    aiAssistant: 'AI Assistant',
    aiPlaceholder: 'Ask anything about your work, projects, policies…',
    aiSend: 'Send',
    aiWelcome: 'Hi! I\'m your Groupe Ouambo AI Assistant. How can I help you today?',
    aiThinking: 'Thinking…',
    aiError: 'Unable to reach AI service. Please try again.',
    itSupportTitle: 'IT Support Chat',
    itSupportDesc: 'Send a message to the IT team. We respond within 24 hours.',
    itSupportPlaceholder: 'Describe your issue…',
    itSupportSend: 'Send',
    teamDirTitle: 'Team Directory',
    teamDirDesc: 'All active team members at Groupe Ouambo.',
    comingSoon: 'This section is being set up.',
    comingSoonDesc: 'Content will be available soon. Contact your admin for urgent needs.',
    statusLabels: {
      planning: 'Planning',
      active: 'Active',
      'on-hold': 'On Hold',
      on_hold: 'On Hold',
      completed: 'Completed'
    }
  },
  fr: {
    dashboardTitle: 'Tableau de bord Employé',
    portalSub: 'Groupe Ouambo — Portail du personnel',
    fullName: 'Nom complet',
    selectName: 'Sélectionnez votre nom',
    password: 'Mot de passe',
    yourPassword: 'Votre mot de passe',
    signIn: 'Se connecter',
    backToWebsite: '← Retour au site',
    signOut: 'Se déconnecter',
    website: '← Site',
    myProjects: 'Mes Projets',
    training: 'Formation',
    activityFeed: 'Fil d\'activité',
    profile: 'Profil',
    projectDetail: 'Détail du projet',
    backToProjects: '← Retour aux projets',
    assigned: 'Assignés',
    active: 'Actif',
    done: 'Terminé',
    comments: 'Commentaires',
    noProjectsAssigned: 'Aucun projet assigné pour l\'instant. Contactez votre administrateur.',
    progress: 'Progression',
    completion: 'Achèvement',
    logProgressUpdate: 'Enregistrer une mise à jour',
    note: 'Note',
    status: 'Statut',
    keepCurrent: 'Garder le statut actuel',
    submitUpdate: 'Soumettre',
    updateHistory: 'Historique des mises à jour',
    noUpdatesYet: 'Aucune mise à jour pour l\'instant.',
    discussion: 'Discussion',
    noCommentsYet: 'Pas encore de commentaires.',
    writeAComment: 'Écrire un commentaire...',
    postComment: 'Publier un commentaire',
    myTraining: 'Ma Formation',
    coursesAssigned: 'Cours assignés par l\'administrateur. Cliquez pour commencer ou continuer.',
    inProgress: 'En cours',
    completed: 'Terminé',
    noCoursesAssigned: 'Aucun cours assigné pour l\'instant. Contactez votre administrateur.',
    startCourse: 'Commencer le cours',
    continueCourse: 'Continuer le cours',
    reviewCourse: 'Revoir le cours',
    badgeEarned: 'Badge obtenu',
    earnedBadges: 'Badges obtenus',
    activityFeedTitle: 'Fil d\'activité',
    recentActivity: 'Mises à jour récentes et commentaires sur vos projets.',
    noActivityYet: 'Aucune activité pour l\'instant. Commencez à travailler sur vos projets.',
    progressUpdate: 'Mise à jour',
    comment: 'Commentaire',
    myProfile: 'Mon Profil',
    updateProfileDesc: 'Mettez à jour votre photo de profil et votre mot de passe.',
    changePhoto: 'Changer la photo',
    changePassword: 'Changer le mot de passe',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    enterNewPassword: 'Entrez le nouveau mot de passe',
    reenterNewPassword: 'Ressaisissez le nouveau mot de passe',
    updatePassword: 'Mettre à jour le mot de passe',
    home: 'Accueil',
    welcomeBack: 'Bon retour',
    quickStats: 'Vue Rapide',
    quickLinks: 'Liens Rapides',
    aiAssistant: 'Assistant IA',
    aiPlaceholder: 'Posez une question sur votre travail, projets, politiques…',
    aiSend: 'Envoyer',
    aiWelcome: "Bonjour ! Je suis votre Assistant IA Groupe Ouambo. Comment puis-je vous aider ?",
    aiThinking: 'Réflexion…',
    aiError: "Impossible de joindre le service IA. Réessayez.",
    itSupportTitle: 'Support Informatique',
    itSupportDesc: "Envoyez un message à l'équipe IT. Nous répondons sous 24h.",
    itSupportPlaceholder: 'Décrivez votre problème…',
    itSupportSend: 'Envoyer',
    teamDirTitle: "Annuaire d'Équipe",
    teamDirDesc: 'Tous les membres actifs du Groupe Ouambo.',
    comingSoon: 'Cette section est en cours de configuration.',
    comingSoonDesc: 'Le contenu sera bientôt disponible. Contactez votre admin pour les besoins urgents.',
    statusLabels: {
      planning: 'En planification',
      active: 'Actif',
      'on-hold': 'En attente',
      on_hold: 'En attente',
      completed: 'Terminé'
    }
  }
}
const PROJECT_STATUSES = ['planning', 'active', 'on-hold', 'completed']

const WEEKDAYS_EN = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const WEEKDAYS_FR = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam']
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December']
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']

function getInitials(name = '') {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}


// Scroll only inside a chat panel — never yank the window scroll position.
function scrollChatToBottom(endRef, { force = false } = {}) {
  const end = endRef?.current
  if (!end) return
  const container = end.parentElement
  if (!container) return
  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
  if (!force && distanceFromBottom > 96) return
  container.scrollTop = container.scrollHeight
}

export default function WorkerDashboard() {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || 'fr')
  const t = translations[lang]

  const [session, setSession] = useState(() => {
    const s = localStorage.getItem(SESSION_KEY)
    return s ? JSON.parse(s) : null
  })
  const [workers, setWorkers]       = useState([])
  const [projects, setProjects]     = useState([])
  const [allTraining, setAllTraining]       = useState([])
  const [allAssignments, setAllAssignments] = useState([])
  const [courseProgress, setCourseProgress] = useState([])

  // login
  const [loginName, setLoginName]         = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError]       = useState('')

  // nav
  const [activeTab, setActiveTabRaw]          = useState(() => localStorage.getItem('wd_tab') || 'home')
  const setActiveTab = (tab) => { setActiveTabRaw(tab); localStorage.setItem('wd_tab', tab) }
  const [sidebarOpen, setSidebarOpen]         = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const [activeCourse, setActiveCourse]       = useState(null)
  const [openMenuSection, setOpenMenuSection] = useState(null)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)

  // AI assistant
  const [aiMessages, setAiMessages] = useState([])
  const [aiInput, setAiInput]       = useState('')
  const [aiLoading, setAiLoading]   = useState(false)
  const aiEndRef = useRef(null)

  // IT Support chat (Firestore)
  const [itMessages, setItMessages] = useState([])
  const [itInput, setItInput]       = useState('')
  const itEndRef = useRef(null)

  // Customer chat (worker support agent)
  const [custSessions, setCustSessions]       = useState([])
  const [custActiveSession, setCustActiveSession] = useState(null)
  const [custSessionMsgs, setCustSessionMsgs] = useState([])
  const [custReply, setCustReply]             = useState('')
  const custEndRef  = useRef(null)
  const custPollRef = useRef(null)
  const custMsgPollRef = useRef(null)
  const sidebarRef = useRef(null)
  const sidebarScrollTopRef = useRef(0)

  // Schedule / Calendar
  const [calYear, setCalYear] = useState(() => new Date().getFullYear())
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth())

  // Time Off
  const [timeOffRequests, setTimeOffRequests] = useState([])
  const [timeOffForm, setTimeOffForm] = useState({ type: 'vacation', startDate: '', endDate: '', reason: '' })
  const [timeOffMsg, setTimeOffMsg] = useState('')
  const [timeOffLoading, setTimeOffLoading] = useState(false)

  // progress update form
  const [updateNote, setUpdateNote]         = useState('')
  const [updateProgress, setUpdateProgress] = useState('')
  const [updateStatus, setUpdateStatus]     = useState('')
  const [updateMsg, setUpdateMsg]           = useState('')

  // comment
  const [commentText, setCommentText] = useState('')
  const [commentMsg, setCommentMsg]   = useState('')
  const commentsEndRef = useRef(null)

  // profile
  const [profileMsg, setProfileMsg]         = useState('')
  const [newPassword, setNewPassword]       = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const avatarInputRef = useRef(null)
  const profileDropdownRef = useRef(null)

  // Notifications
  const [workerNotifications, setWorkerNotifications] = useState([])
  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef(null)

  /* ── Load data ─────────────────────────────────────────────── */
  useEffect(() => {
  }, [])

  // Load own full profile (email/phone/region) once logged in
  useEffect(() => {
    if (!session) return
    workerPortalAPI.me().then(me => {
      setWorkers(prev => prev.some(w => w.id === me.id) ? prev.map(w => w.id === me.id ? { ...w, ...me } : w) : [...prev, me])
    }).catch(() => { setSession(null); localStorage.removeItem(SESSION_KEY) })
  }, [session?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (session) projectsAPI.getAll().then(setProjects).catch(console.error) }, [session?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keep sidebar menu scroll position across re-renders (notifications/polls).
  useLayoutEffect(() => {
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = sidebarScrollTopRef.current
    }
  })

  useEffect(() => {
    trainingAPI.getAll().then(setAllTraining).catch(console.error)
    trainingAPI.getAssignments().then(setAllAssignments).catch(console.error)
  }, [])

  useEffect(() => {
    if (session?.id) courseProgressAPI.getByWorker(session.id).then(setCourseProgress).catch(console.error)
  }, [session?.id])

  useEffect(() => {
    scrollChatToBottom(commentsEndRef, { force: true })
  }, [selectedProject])

  useEffect(() => {
    if (!profileDropdownOpen) return
    const handler = (e) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setProfileDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [profileDropdownOpen])

  // IT Support chat listener
  useEffect(() => {
    if (!session?.id || activeTab !== 'it-support') return
    const q = query(
      collection(db, 'it_support_messages'),
      where('workerId', '==', String(session.id)),
      orderBy('timestamp', 'asc')
    )
    const unsub = onSnapshot(q, snap => {
      setItMessages(snap.docs.map(d => ({ id: d.id, ...d.data(), timestamp: d.data().timestamp?.toDate?.()?.toISOString() || new Date().toISOString() })))
      setTimeout(() => scrollChatToBottom(itEndRef, { force: true }), 50)
    }, err => console.error('IT chat error', err))
    return () => unsub()
  }, [session?.id, activeTab])

  useEffect(() => {
    if (!session?.id) return
    const q = query(
      collection(db, 'time_off_requests'),
      where('workerId', '==', String(session.id)),
      orderBy('requestedAt', 'desc')
    )
    return onSnapshot(q, snap => {
      setTimeOffRequests(snap.docs.map(d => ({
        id: d.id, ...d.data(),
        requestedAt: d.data().requestedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })))
    }, console.error)
  }, [session?.id])

  // Worker notifications listener
  useEffect(() => {
    if (!session?.id) return
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', String(session.id))
    )
    return onSnapshot(q, snap => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(d => d.recipientType === 'worker')
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      setWorkerNotifications(docs)
    }, console.error)
  }, [session?.id])

  // Close notif dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [notifOpen])

  useEffect(() => {
    scrollChatToBottom(aiEndRef, { force: true })
  }, [aiMessages])

  // Customer chat polling (only if worker has chat_access)
  useEffect(() => {
    if (!session?.chat_access) return
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    const fetchSessions = () => {
      fetch(`${API_BASE}/visitors/chat-sessions`)
        .then(r => r.json())
        .then(rows => setCustSessions(rows.map(r => ({
          sessionId: r.session_id,
          name: r.name || 'Visitor',
          lastMessage: r.last_message,
          lastSender: r.last_sender,
          lastAt: r.last_at,
          messageCount: r.message_count,
        }))))
        .catch(console.error)
    }
    fetchSessions()
    custPollRef.current = setInterval(fetchSessions, 5000)
    return () => clearInterval(custPollRef.current)
  }, [session?.chat_access])

  useEffect(() => {
    clearInterval(custMsgPollRef.current)
    if (!custActiveSession) return
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    const fetchMsgs = () => {
      fetch(`${API_BASE}/visitors/chat/${custActiveSession}`)
        .then(r => r.json())
        .then(msgs => { setCustSessionMsgs(prev => {
            const grew = !Array.isArray(prev) || msgs.length > prev.length
            if (grew) setTimeout(() => scrollChatToBottom(custEndRef), 50)
            return msgs
          }) })
        .catch(console.error)
    }
    fetchMsgs()
    custMsgPollRef.current = setInterval(fetchMsgs, 4000)
    return () => clearInterval(custMsgPollRef.current)
  }, [custActiveSession])

  /* ── Helpers ───────────────────────────────────────────────── */
  const saveProjects = async (updated, changedId, fields) => {
    setProjects(updated)
    if (selectedProject) {
      const refreshed = updated.find(p => p.id === selectedProject.id)
      if (refreshed) setSelectedProject(refreshed)
    }
    if (changedId && fields) {
      try { await projectsAPI.update(changedId, fields) } catch (e) { console.error(e) }
    }
  }

  const saveWorkerField = async (workerId, fields) => {
    try {
      const updated = await workerPortalAPI.updateMe(fields)
      setWorkers(prev => prev.map(w => w.id === workerId ? { ...w, ...updated, ...fields } : w))
      if (session?.id === workerId) {
        const newSession = { ...session, ...fields }
        setSession(newSession)
        localStorage.setItem(SESSION_KEY, JSON.stringify(newSession))
      }
    } catch (err) { console.error(err) }
  }

  /* ── Derived: my courses ───────────────────────────────────── */
  const myCourses = (() => {
    if (!session) return []
    return allAssignments
      .filter(a => String(a.worker_id ?? a.workerId) === String(session.id))
      .map(a => {
        const cId = a.course_id ?? a.courseId
        const course = allTraining.find(t => t.id === cId)
        if (!course) return null
        const prog = courseProgress.find(p => p.course_id === cId || p.courseId === cId)
        return { ...course, assignedAt: a.assigned_at ?? a.assignedAt, pct: prog ? (prog.pct || 0) : 0 }
      })
      .filter(Boolean)
  })()

  const earnedBadges = myCourses.filter(c => c.pct === 100)

  /* ── Derived: activity feed ────────────────────────────────── */
  const feedItems = (() => {
    if (!session) return []
    const myProjs = projects.filter(p => String(p.assignedTo ?? p.assigned_to) === String(session.id))
    const items = []
    myProjs.forEach(proj => {
      ;(proj.updates || []).forEach(u => items.push({ type: 'update', proj: proj.title, text: u.note, by: u.by, at: u.at, pct: u.progress }))
      ;(proj.comments || []).forEach(c => items.push({ type: 'comment', proj: proj.title, text: c.text, by: c.by, role: c.role, at: c.at }))
    })
    return items.sort((a, b) => new Date(b.at) - new Date(a.at))
  })()

  /* ── Auth ──────────────────────────────────────────────────── */
  const logWorkerAccess = (worker) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    const payload = {
      workerId: String(worker.id),
      workerName: worker.name,
      department: worker.department || '',
      device: navigator.userAgent.slice(0, 200),
    }
    const doLog = (extra = {}) => {
      workerPortalAPI.track({ ...payload, ...extra }).catch(console.error)
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => doLog({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => doLog(),
        { timeout: 5000 }
      )
    } else {
      doLog()
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    let match
    try {
      const res = await workerPortalAPI.login(loginName, loginPassword)
      match = res.worker
    } catch (err) {
      setLoginError(lang === 'fr' ? 'Identifiants invalides.' : 'Invalid credentials.'); return
    }
    setWorkers(prev => prev.some(w => w.id === match.id) ? prev.map(w => w.id === match.id ? { ...w, ...match } : w) : [...prev, match])
    const s = { id: match.id, name: match.name, department: match.department, avatar: match.avatar || null, chat_access: !!match.chat_access }
    setSession(s)
    localStorage.setItem(SESSION_KEY, JSON.stringify(s))
    logWorkerAccess(match)
    setLoginError('')
  }

  const handleLogout = () => {
    setSession(null)
    localStorage.removeItem(SESSION_KEY)
    workerPortalAPI.logout().catch(() => {})
    setSelectedProject(null)
    setActiveCourse(null)
  }

  const dismissWorkerNotif = async (id) => {
    try { await deleteDoc(doc(db, 'notifications', id)) } catch (err) { console.error(err) }
  }

  const handleCustSendReply = async e => {
    e.preventDefault()
    if (!custReply.trim() || !custActiveSession) return
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    try {
      await fetch(`${API_BASE}/visitors/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: custActiveSession, sender: 'admin', name: session.name, text: custReply.trim() }),
      })
      setCustReply('')
    } catch (err) { console.error(err) }
  }

  /* ── Progress update ───────────────────────────────────────── */
  const handleAddUpdate = (e) => {
    e.preventDefault()
    if (!updateNote.trim() || !selectedProject) return
    const newProg   = updateProgress !== '' ? parseInt(updateProgress) : selectedProject.progress
    const newStatus = updateStatus || selectedProject.status
    const upd = {
      id: Date.now().toString(36),
      note: updateNote.trim(),
      progress: newProg,
      status: newStatus,
      by: session.name,
      at: new Date().toISOString()
    }
    const newUpdates = [...(selectedProject.updates || []), upd]
    const updated = projects.map(p =>
      p.id !== selectedProject.id ? p : { ...p, progress: newProg, status: newStatus, updates: newUpdates }
    )
    saveProjects(updated, selectedProject.id, { progress: newProg, status: newStatus, updates: newUpdates })
    setUpdateNote(''); setUpdateProgress(''); setUpdateStatus('')
    setUpdateMsg('Update logged!'); setTimeout(() => setUpdateMsg(''), 3000)
  }

  /* ── Comment ───────────────────────────────────────────────── */
  const handleAddComment = (e) => {
    e.preventDefault()
    if (!commentText.trim() || !selectedProject) return
    const comment = {
      id: Date.now().toString(36),
      text: commentText.trim(),
      by: session.name,
      role: 'worker',
      at: new Date().toISOString()
    }
    const newComments = [...(selectedProject.comments || []), comment]
    const updated = projects.map(p =>
      p.id !== selectedProject.id ? p : { ...p, comments: newComments }
    )
    saveProjects(updated, selectedProject.id, { comments: newComments })
    setCommentText('')
    setCommentMsg('Comment posted!'); setTimeout(() => setCommentMsg(''), 2000)
    setTimeout(() => scrollChatToBottom(commentsEndRef, { force: true }), 50)
  }

  /* ── AI Assistant ──────────────────────────────────────────── */
  const handleAiSend = async (e) => {
    e.preventDefault()
    const text = aiInput.trim()
    if (!text) return
    setAiInput('')
    const userMsg = { role: 'user', content: text, at: new Date().toISOString() }
    setAiMessages(prev => [...prev, userMsg])
    setAiLoading(true)
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
      const res = await fetch(`${API_BASE}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, workerName: session.name })
      })
      const data = await res.json()
      setAiMessages(prev => [...prev, { role: 'assistant', content: data.reply || t.aiError, at: new Date().toISOString() }])
    } catch {
      setAiMessages(prev => [...prev, { role: 'assistant', content: t.aiError, at: new Date().toISOString() }])
    } finally { setAiLoading(false) }
  }

  /* ── IT Support send ────────────────────────────────────────── */
  const handleItSend = async (e) => {
    e.preventDefault()
    const text = itInput.trim()
    if (!text || !session) return
    setItInput('')
    try {
      await addDoc(collection(db, 'it_support_messages'), {
        workerId: String(session.id),
        workerName: session.name,
        text,
        sender: 'worker',
        timestamp: serverTimestamp()
      })
      // Notify admin of new IT support message
      await addDoc(collection(db, 'notifications'), {
        recipientType: 'admin',
        type: 'it_support',
        message: `IT support message from ${session.name}: "${text.slice(0, 60)}${text.length > 60 ? '…' : ''}"`,
        read: false,
        createdAt: serverTimestamp(),
      })
      setTimeout(() => scrollChatToBottom(itEndRef, { force: true }), 100)
    } catch (err) { console.error('IT send error', err) }
  }

  /* ── Time Off submit ───────────────────────────────────────── */
  const handleTimeOffSubmit = async (e) => {
    e.preventDefault()
    if (!timeOffForm.startDate || !timeOffForm.endDate) return
    setTimeOffLoading(true)
    try {
      await addDoc(collection(db, 'time_off_requests'), {
        workerId: String(session.id),
        workerName: session.name,
        department: session.department || '',
        type: timeOffForm.type,
        startDate: timeOffForm.startDate,
        endDate: timeOffForm.endDate,
        reason: timeOffForm.reason,
        status: 'pending',
        requestedAt: serverTimestamp(),
      })
      // Notify admin
      await addDoc(collection(db, 'notifications'), {
        recipientType: 'admin',
        type: 'timeoff_request',
        message: `${session.name} requested time off (${timeOffForm.type}) from ${timeOffForm.startDate} to ${timeOffForm.endDate}.`,
        read: false,
        createdAt: serverTimestamp(),
      })
      setTimeOffForm({ type: 'vacation', startDate: '', endDate: '', reason: '' })
      setTimeOffMsg(lang === 'fr' ? 'Demande soumise avec succès !' : 'Request submitted successfully!')
      setTimeout(() => setTimeOffMsg(''), 4000)
    } catch (err) {
      console.error(err)
      setTimeOffMsg(lang === 'fr' ? 'Erreur lors de la soumission.' : 'Error submitting request.')
    } finally { setTimeOffLoading(false) }
  }

  /* ── Profile: password ─────────────────────────────────────── */
  const handleChangePassword = async (e) => {
    e.preventDefault()
    if (!newPassword.trim()) { setProfileMsg('Enter a new password.'); return }
    if (newPassword !== confirmPassword) { setProfileMsg('Passwords do not match.'); return }
    await saveWorkerField(session.id, { password: newPassword })
    setNewPassword(''); setConfirmPassword('')
    setProfileMsg('Password updated successfully!'); setTimeout(() => setProfileMsg(''), 3000)
  }

  /* ── Profile: avatar ───────────────────────────────────────── */
  const handleAvatarChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = async () => {
      await saveWorkerField(session.id, { avatar: reader.result })
      setProfileMsg('Profile picture updated!'); setTimeout(() => setProfileMsg(''), 3000)
    }
    reader.readAsDataURL(file)
  }

  const myWorker  = workers.find(w => w.id === session?.id)
  const myProjects = projects.filter(p => String(p.assignedTo ?? p.assigned_to) === String(session?.id))
  const workerDept = String(session?.department || myWorker?.department || '').toLowerCase()
  const canManageBilling = ['admin', 'sales', 'administration'].includes(workerDept)

  /* ── Login screen ──────────────────────────────────────────── */
  if (!session) {
    return (
      <div className="wd-login-screen">
        <form className="wd-login-card" onSubmit={handleLogin}>
          <div className="wd-login-logo">
            <span className="wd-login-icon">👷</span>
            <h1>{t.dashboardTitle}</h1>
            <p>{t.portalSub}</p>
          </div>
          <div className="wd-lang-toggle" style={{ marginBottom: '-8px' }}>
            <button type="button" className={lang === 'fr' ? 'active' : ''} onClick={() => { const n = 'fr'; localStorage.setItem(LANG_KEY, n); setLang(n) }}>FR</button>
            <button type="button" className={lang === 'en' ? 'active' : ''} onClick={() => { const n = 'en'; localStorage.setItem(LANG_KEY, n); setLang(n) }}>EN</button>
          </div>
          <div className="wd-login-fields">
            <label>
              {lang === 'fr' ? 'E-mail ou nom d\'utilisateur' : 'Email or username'}
              <input type="text" autoComplete="username" value={loginName} onChange={e => setLoginName(e.target.value)} placeholder="name@groupeouambo.com" required />
            </label>
            <label>
              {t.password}
              <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder={t.yourPassword} required />
            </label>
            {loginError && <p className="wd-login-error">{loginError}</p>}
            <button type="submit" className="wd-login-btn">{t.signIn}</button>
          </div>
          <Link to="/" className="wd-login-back">{t.backToWebsite}</Link>
        </form>
      </div>
    )
  }

  /* ── Full course view ──────────────────────────────────────── */
  if (activeCourse) {
    if (activeCourse.id === 'default-pmp-1') {
      return (
        <ProjectManagementCoursePro
          workerId={session.id}
          courseId={activeCourse.id}
          onBack={() => setActiveCourse(null)}
        />
      )
    }
    if (activeCourse.id === 'default-logistics-1') {
      return (
        <LogisticsCoursePro
          workerId={session.id}
          courseId={activeCourse.id}
          onBack={() => setActiveCourse(null)}
        />
      )
    }
    if (activeCourse.id === 'english-1000h') {
      return (
        <EnglishCoursePro
          workerId={session.id}
          courseId={activeCourse.id}
          onBack={() => setActiveCourse(null)}
        />
      )
    }
    if (activeCourse.id === 'leadership-16') {
      return (
        <LeadershipCoursePro
          workerId={session.id}
          courseId={activeCourse.id}
          onBack={() => setActiveCourse(null)}
        />
      )
    }
    return (
      <div className="wd-layout">
        <main className="wd-main">
          <div className="wd-topbar">
            <button className="wd-back-btn" onClick={() => setActiveCourse(null)}>← Back</button>
            <span className="wd-topbar-title">{activeCourse.title}</span>
          </div>
          <div className="wd-content">
            <div className="wd-section">
              <div className="wd-empty-state"><span>📚</span><p>Course content coming soon.</p></div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  /* ── Sidebar ───────────────────────────────────────────────── */
  const Sidebar = () => (
    <aside ref={sidebarRef} className={`wd-sidebar ${sidebarOpen ? 'open' : ''}`} onScroll={() => { if (sidebarRef.current) sidebarScrollTopRef.current = sidebarRef.current.scrollTop }}>
      <div className="wd-sidebar-brand">
        <div className="wd-sidebar-avatar">
          {(session.avatar || myWorker?.avatar)
            ? <img src={session.avatar || myWorker?.avatar} alt="avatar" />
            : <span>{getInitials(session.name)}</span>
          }
        </div>
        <div className="wd-sidebar-identity">
          <span className="wd-sidebar-name">{session.name}</span>
          <span className="wd-sidebar-dept">{session.department?.replace(/_/g, ' ')}</span>
        </div>
        <button className="wd-sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
      </div>
      <nav className="wd-nav">
        {[
          { key: 'home',            icon: '🏠', label: t.home || 'Home' },
          { key: 'projects',        icon: '📁', label: t.myProjects,   badge: myProjects.length },
          { key: 'feed',            icon: '📰', label: t.activityFeed, badge: feedItems.length > 0 ? feedItems.length : null },
          ...(session?.chat_access ? [{ key: 'customer-chat', icon: '💬', label: lang === 'fr' ? 'Chat Clients' : 'Customer Chat', badge: custSessions.filter(s => s.lastSender === 'visitor').length || null }] : []),
          ...(canManageBilling ? [
            { key: 'quotes', icon: '📝', label: lang === 'fr' ? 'Devis' : 'Quotes' },
            { key: 'invoices', icon: '🧾', label: lang === 'fr' ? 'Factures' : 'Invoices' },
          ] : []),
          { key: 'code-of-conduct', icon: '📜', label: lang === 'fr' ? 'Politique' : 'Policy' },
          { key: 'safety-guidelines',icon: '🛡️', label: lang === 'fr' ? 'Sécurité' : 'Safety' },
          { key: 'profile',         icon: '👤', label: t.profile },
        ].map(item => (
          <button
            key={item.key}
            className={`wd-nav-item ${activeTab === item.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(item.key); setSelectedProject(null); setSidebarOpen(false) }}
          >
            <span className="wd-nav-icon">{item.icon}</span>
            <span className="wd-nav-label">{item.label}</span>
            {item.badge ? <span className="wd-nav-badge">{item.badge}</span> : null}
          </button>
        ))}

        <div className="wd-nav-divider" />

        {MENU_SECTIONS.map(section => (
          <div key={section.key} className="wd-nav-section">
            <button
              className={`wd-nav-item wd-nav-section-trigger ${openMenuSection === section.key ? 'active' : ''}`}
              onClick={() => setOpenMenuSection(prev => prev === section.key ? null : section.key)}
            >
              <span className="wd-nav-icon">{section.icon}</span>
              <span className="wd-nav-label">{section.label[lang]}</span>
              <span className={`wd-nav-arrow ${openMenuSection === section.key ? 'open' : ''}`}>▾</span>
            </button>
            {openMenuSection === section.key && (
              <div className="wd-nav-dropdown">
                {section.items.map((item, i) => (
                  <button key={i} className="wd-nav-dropdown-item"
                    onClick={() => { if (item.tab) { setActiveTab(item.tab); setSelectedProject(null); setSidebarOpen(false) } }}
                  >
                    {item[lang]}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
      <div className="wd-sidebar-footer">
        <Link to="/" className="wd-sidebar-link">{t.website}</Link>
      </div>
    </aside>
  )

  /* ── Project detail ────────────────────────────────────────── */
  if (selectedProject) {
    const proj = projects.find(p => p.id === selectedProject.id) || selectedProject
    const allComments  = [...(proj.comments || [])].sort((a, b) => new Date(a.at) - new Date(b.at))
    const sortedUpdates = [...(proj.updates || [])].reverse()
    return (
      <div className="wd-layout">
        <Sidebar />
        {sidebarOpen && <div className="wd-overlay" onClick={() => setSidebarOpen(false)} />}
        <main className="wd-main">
          <div className="wd-topbar">
            <button className="wd-menu-btn" onClick={() => setSidebarOpen(true)}>☰</button>
            <span className="wd-topbar-title">{t.projectDetail}</span>
            <div className="wd-topbar-actions">
              <div className="wd-lang-toggle">
                <button className={lang === 'fr' ? 'active' : ''} onClick={() => setLang(l => { const n = l === 'fr' ? 'en' : 'fr'; localStorage.setItem(LANG_KEY, n); return n })}>FR</button>
                <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang(l => { const n = l === 'en' ? 'fr' : 'en'; localStorage.setItem(LANG_KEY, n); return n })}>EN</button>
              </div>
              <div className="notif-wrap" ref={notifRef}>
                <button className="notif-bell-btn" onClick={() => setNotifOpen(o => !o)}>
                  🔔{workerNotifications.length > 0 && <span className="notif-badge">{workerNotifications.length}</span>}
                </button>
                {notifOpen && (
                  <div className="notif-dropdown">
                    <div className="notif-dropdown-header">Notifications</div>
                    {workerNotifications.length === 0 ? <div className="notif-empty">No new notifications</div> : workerNotifications.map(n => (
                      <div key={n.id} className="notif-item"><span className="notif-msg">{n.message}</span><button className="notif-dismiss" onClick={() => dismissWorkerNotif(n.id)}>✕</button></div>
                    ))}
                  </div>
                )}
              </div>
              <div className="wd-topbar-profile-wrap" ref={profileDropdownRef}>
                <button className="wd-topbar-profile-btn" onClick={() => setProfileDropdownOpen(o => !o)}>
                  <div className="wd-topbar-avatar">
                    {(session.avatar || myWorker?.avatar)
                      ? <img src={session.avatar || myWorker?.avatar} alt="avatar" />
                      : <span>{getInitials(session.name)}</span>}
                  </div>
                  <span className="wd-topbar-user">{session.name}</span>
                  <span className="wd-topbar-arrow">{profileDropdownOpen ? '▴' : '▾'}</span>
                </button>
                {profileDropdownOpen && (
                  <div className="wd-topbar-dropdown">
                    <button className="wd-topbar-dropdown-item" onClick={() => { setActiveTab('profile'); setSelectedProject(null); setProfileDropdownOpen(false) }}>
                      👤 {t.profile}
                    </button>
                    <button className="wd-topbar-dropdown-item danger" onClick={() => { handleLogout(); setProfileDropdownOpen(false) }}>
                      🚪 {t.signOut}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="wd-content">
            <div className="wd-section">
              <button className="wd-back-btn" onClick={() => setSelectedProject(null)}>{t.backToProjects}</button>

              <div className="wd-detail-header">
                <div className="wd-detail-meta-row">
                  <span className={`wd-status-badge ${proj.status}`}>{t.statusLabels[proj.status] || proj.status}</span>
                  <span className="wd-rubric-badge">{proj.rubric}</span>
                </div>
                <h2 className="wd-detail-title">{proj.title}</h2>
                {proj.description && <p className="wd-detail-desc">{proj.description}</p>}
              </div>

              <div className="wd-detail-grid">
                <div className="wd-detail-left">
                  <div className="wd-card">
                    <h4>{t.progress}</h4>
                    <div className="wd-progress-wrap">
                      <div className="wd-progress-label"><span>{t.completion}</span><strong>{proj.progress}%</strong></div>
                      <div className="wd-progress-bar">
                        <div className={`wd-progress-fill ${proj.progress === 100 ? 'complete' : proj.progress < 25 ? 'low' : ''}`}
                          style={{ width: `${proj.progress}%` }} />
                      </div>
                    </div>
                    <div className="wd-meta-list">
                      {proj.dueDate && <span>📅 Due {new Date(proj.dueDate).toLocaleDateString()}</span>}
                      <span>🔄 {(proj.updates || []).length} updates</span>
                    </div>
                  </div>

                  <div className="wd-card">
                    <h4>{t.logProgressUpdate}</h4>
                    <form className="wd-update-form" onSubmit={handleAddUpdate}>
                      <label>
                        {t.note} <span className="wd-required">*</span>
                        <textarea value={updateNote} onChange={e => setUpdateNote(e.target.value)} placeholder="What was done, blockers, next steps..." rows={3} required />
                      </label>
                      <div className="wd-form-row">
                        <label>
                          {t.progress} (%)
                          <input type="number" min="0" max="100" value={updateProgress} onChange={e => setUpdateProgress(e.target.value)} placeholder={`Current: ${proj.progress}%`} />
                        </label>
                        <label>
                          {t.status}
                          <select value={updateStatus} onChange={e => setUpdateStatus(e.target.value)}>
                            <option value="">{t.keepCurrent}</option>
                            {PROJECT_STATUSES.map(s => <option key={s} value={s}>{t.statusLabels[s] || s}</option>)}
                          </select>
                        </label>
                      </div>
                      <div className="wd-form-actions">
                        <button type="submit" className="wd-btn primary">{t.submitUpdate}</button>
                        {updateMsg && <span className="wd-status-msg">{updateMsg}</span>}
                      </div>
                    </form>
                  </div>

                  <div className="wd-card">
                    <h4>{t.updateHistory} ({(proj.updates || []).length})</h4>
                    {sortedUpdates.length === 0
                      ? <p className="wd-empty-msg">{t.noUpdatesYet}</p>
                      : <div className="wd-timeline">
                          {sortedUpdates.map(u => (
                            <div key={u.id} className="wd-timeline-item">
                              <div className="wd-timeline-dot" />
                              <div className="wd-timeline-body">
                                <p className="wd-timeline-note">{u.note}</p>
                                <div className="wd-timeline-meta">
                                  <span>{u.by}</span>
                                  <span>{new Date(u.at).toLocaleString()}</span>
                                  <span className="wd-timeline-pct">{u.progress}%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                    }
                  </div>
                </div>

                <div className="wd-comments-panel">
                  <h4>{t.discussion} ({allComments.length})</h4>
                  <div className="wd-comments-list">
                    {allComments.length === 0
                      ? <p className="wd-empty-msg">{t.noCommentsYet}</p>
                      : allComments.map(c => (
                          <div key={c.id} className={`wd-comment ${c.role}`}>
                            <div className="wd-comment-header">
                              <span className="wd-comment-author">{c.by}</span>
                              <span className={`wd-comment-role ${c.role}`}>{c.role}</span>
                              <span className="wd-comment-time">{new Date(c.at).toLocaleString()}</span>
                            </div>
                            <p className="wd-comment-text">{c.text}</p>
                          </div>
                        ))
                    }
                    <div ref={commentsEndRef} />
                  </div>
                  <form className="wd-comment-form" onSubmit={handleAddComment}>
                    <textarea
                      className="wd-comment-input"
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      placeholder={t.writeAComment}
                      rows={3}
                      required
                    />
                    <div className="wd-form-actions">
                      <button type="submit" className="wd-btn primary small">{t.postComment}</button>
                      {commentMsg && <span className="wd-status-msg">{commentMsg}</span>}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  /* ── Main dashboard ────────────────────────────────────────── */
  return (
    <div className="wd-layout">
      <Sidebar />
      {sidebarOpen && <div className="wd-overlay" onClick={() => setSidebarOpen(false)} />}

      <main className="wd-main">
        <div className="wd-topbar">
          <button className="wd-menu-btn" onClick={() => setSidebarOpen(true)}>☰</button>
          <span className="wd-topbar-title">
            {TAB_TITLES[lang]?.[activeTab] || activeTab}
          </span>
          <div className="wd-topbar-actions">
            <div className="wd-lang-toggle">
              <button className={lang === 'fr' ? 'active' : ''} onClick={() => setLang(l => { const n = l === 'fr' ? 'en' : 'fr'; localStorage.setItem(LANG_KEY, n); return n })}>FR</button>
              <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang(l => { const n = l === 'en' ? 'fr' : 'en'; localStorage.setItem(LANG_KEY, n); return n })}>EN</button>
            </div>
            {/* Notification bell */}
            <div className="notif-wrap" ref={notifRef}>
              <button className="notif-bell-btn" onClick={() => setNotifOpen(o => !o)}>
                🔔
                {workerNotifications.length > 0 && (
                  <span className="notif-badge">{workerNotifications.length}</span>
                )}
              </button>
              {notifOpen && (
                <div className="notif-dropdown">
                  <div className="notif-dropdown-header">{lang === 'fr' ? 'Notifications' : 'Notifications'}</div>
                  {workerNotifications.length === 0 ? (
                    <div className="notif-empty">{lang === 'fr' ? 'Aucune notification' : 'No new notifications'}</div>
                  ) : (
                    workerNotifications.map(n => (
                      <div key={n.id} className="notif-item">
                        <span className="notif-msg">{n.message}</span>
                        <button className="notif-dismiss" onClick={() => dismissWorkerNotif(n.id)}>✕</button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="wd-topbar-profile-wrap" ref={profileDropdownRef}>
              <button className="wd-topbar-profile-btn" onClick={() => setProfileDropdownOpen(o => !o)}>
                <div className="wd-topbar-avatar">
                  {(session.avatar || myWorker?.avatar)
                    ? <img src={session.avatar || myWorker?.avatar} alt="avatar" />
                    : <span>{getInitials(session.name)}</span>}
                </div>
                <span className="wd-topbar-user">{session.name}</span>
                <span className="wd-topbar-arrow">{profileDropdownOpen ? '▴' : '▾'}</span>
              </button>
              {profileDropdownOpen && (
                <div className="wd-topbar-dropdown">
                  <button className="wd-topbar-dropdown-item" onClick={() => { setActiveTab('profile'); setSelectedProject(null); setProfileDropdownOpen(false) }}>
                    👤 {t.profile}
                  </button>
                  <button className="wd-topbar-dropdown-item danger" onClick={() => { handleLogout(); setProfileDropdownOpen(false) }}>
                    🚪 {t.signOut}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="wd-content">

          {/* ── HOME tab ─────────────────────────────────────── */}
          {activeTab === 'home' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>{t.welcomeBack}, {session.name.split(' ')[0]}! 👋</h2>
                <p>{lang === 'fr' ? 'Posez vos questions à votre assistant IA.' : 'Ask your AI assistant anything.'}</p>
              </div>

              {/* AI Assistant — compact centered card */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                <div style={{ width: '100%', maxWidth: 480, background: '#fff', borderRadius: 20, boxShadow: '0 8px 32px rgba(37,99,235,.12)', border: '1px solid #e0e7ff', overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🤖</div>
                    <div>
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>{t.aiAssistant}</div>
                      <div style={{ color: 'rgba(255,255,255,.7)', fontSize: '0.75rem' }}>{lang === 'fr' ? 'Posez n\'importe quelle question' : 'Ask me anything'}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 6px #4ade80' }} />
                  </div>
                  {/* Messages */}
                  <div style={{ height: 280, overflowY: 'auto', padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', gap: 10, background: '#f8fafc' }}>
                    {aiMessages.length === 0 && (
                      <div style={{ alignSelf: 'flex-start', maxWidth: '85%', background: '#fff', border: '1px solid #e0e7ff', borderRadius: '4px 16px 16px 16px', padding: '10px 14px', fontSize: '0.875rem', color: '#1e293b', lineHeight: 1.5, boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
                        {t.aiWelcome}
                      </div>
                    )}
                    {aiMessages.map((m, i) => (
                      <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', background: m.role === 'user' ? '#2563eb' : '#fff', border: m.role === 'user' ? 'none' : '1px solid #e0e7ff', borderRadius: m.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px', padding: '10px 14px', fontSize: '0.875rem', color: m.role === 'user' ? '#fff' : '#1e293b', lineHeight: 1.5, boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
                        {m.content}
                      </div>
                    ))}
                    {aiLoading && (
                      <div style={{ alignSelf: 'flex-start', background: '#fff', border: '1px solid #e0e7ff', borderRadius: '4px 16px 16px 16px', padding: '10px 14px', fontSize: '0.875rem', color: '#94a3b8' }}>
                        <span style={{ display: 'inline-flex', gap: 4 }}>
                          <span style={{ animation: 'blink 1.2s infinite' }}>●</span>
                          <span style={{ animation: 'blink 1.2s .4s infinite' }}>●</span>
                          <span style={{ animation: 'blink 1.2s .8s infinite' }}>●</span>
                        </span>
                      </div>
                    )}
                    <div ref={aiEndRef} />
                  </div>
                  {/* Input */}
                  <form onSubmit={handleAiSend} style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
                    <input
                      value={aiInput}
                      onChange={e => setAiInput(e.target.value)}
                      placeholder={lang === 'fr' ? 'Posez une question…' : 'Ask a question…'}
                      disabled={aiLoading}
                      style={{ flex: 1, padding: '9px 14px', border: '1.5px solid #e0e7ff', borderRadius: 999, fontSize: '0.875rem', outline: 'none', fontFamily: 'inherit', background: '#f8fafc' }}
                    />
                    <button type="submit" disabled={aiLoading || !aiInput.trim()} style={{ padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 999, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', opacity: (aiLoading || !aiInput.trim()) ? 0.5 : 1 }}>
                      {t.aiSend}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* ── PROJECTS tab ─────────────────────────────────── */}
          {activeTab === 'projects' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>{t.myProjects}</h2>
                <p>{lang === 'fr' ? 'Projets qui vous sont assignés. Cliquez pour voir les détails et enregistrer la progression.' : 'Projects assigned to you. Click to view details and log progress.'}</p>
              </div>

              <div className="wd-stats-row">
                {[
                  { icon: '📁', label: t.assigned, value: myProjects.length, color: 'blue' },
                  { icon: '⚡', label: t.active,   value: myProjects.filter(p => p.status === 'active').length, color: 'orange' },
                  { icon: '✅', label: t.done,      value: myProjects.filter(p => p.status === 'completed').length, color: 'green' },
                  { icon: '💬', label: t.comments,  value: myProjects.reduce((s, p) => s + (p.comments || []).length, 0), color: 'purple' },
                ].map(s => (
                  <div key={s.label} className="wd-stat-card">
                    <div className={`wd-stat-icon ${s.color}`}>{s.icon}</div>
                    <div>
                      <p className="wd-stat-label">{s.label}</p>
                      <p className="wd-stat-value">{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {myProjects.length === 0 ? (
                <div className="wd-empty-state">
                  <span>📭</span>
                  <p>{t.noProjectsAssigned}</p>
                </div>
              ) : (
                <div className="wd-projects-grid">
                  {myProjects.map(proj => (
                    <div key={proj.id} className="wd-project-card" onClick={() => setSelectedProject(proj)}>
                      <div className="wd-project-card-top">
                        <span className={`wd-status-badge ${proj.status}`}>{t.statusLabels[proj.status] || proj.status}</span>
                        <span className="wd-rubric-badge">{proj.rubric}</span>
                      </div>
                      <h3 className="wd-project-title">{proj.title}</h3>
                      {proj.description && <p className="wd-project-desc">{proj.description}</p>}
                      <div className="wd-progress-wrap">
                        <div className="wd-progress-label"><span>{t.progress}</span><strong>{proj.progress}%</strong></div>
                        <div className="wd-progress-bar">
                          <div className={`wd-progress-fill ${proj.progress === 100 ? 'complete' : proj.progress < 25 ? 'low' : ''}`}
                            style={{ width: `${proj.progress}%` }} />
                        </div>
                      </div>
                      <div className="wd-project-footer">
                        {proj.dueDate && <span>📅 {new Date(proj.dueDate).toLocaleDateString()}</span>}
                        <span>🔄 {(proj.updates || []).length} updates</span>
                        <span>💬 {(proj.comments || []).length} comments</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── TRAINING tab ──────────────────────────────────── */}
          {activeTab === 'training' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>{t.myTraining}</h2>
                <p>{t.coursesAssigned}</p>
              </div>

              {/* Stats */}
              <div className="wd-stats-row">
                {[
                  { icon: '🎓', label: t.assigned,   value: myCourses.length,                                    color: 'blue' },
                  { icon: '📖', label: t.inProgress, value: myCourses.filter(c => c.pct > 0 && c.pct < 100).length, color: 'orange' },
                  { icon: '🏆', label: t.completed,   value: earnedBadges.length,                                color: 'green' },
                ].map(s => (
                  <div key={s.label} className="wd-stat-card">
                    <div className={`wd-stat-icon ${s.color}`}>{s.icon}</div>
                    <div>
                      <p className="wd-stat-label">{s.label}</p>
                      <p className="wd-stat-value">{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {myCourses.length === 0 ? (
                <div className="wd-empty-state">
                  <span>📚</span>
                  <p>{t.noCoursesAssigned}</p>
                </div>
              ) : (
                <div className="wd-courses-grid">
                  {myCourses.map(course => (
                    <div key={course.id} className="wd-course-card">
                      <div className="wd-course-card-top">
                        <span className={`wd-cat-badge ${course.category}`}>{course.category}</span>
                        {course.pct === 100 && <span className="wd-course-complete-badge">✓ {t.completed}</span>}
                      </div>
                      <h3 className="wd-course-title">{course.title}</h3>
                      <p className="wd-course-desc">{course.description}</p>
                      <div className="wd-course-meta">
                        <span>⏱ {course.duration}</span>
                        <strong>{course.pct}%</strong>
                      </div>
                      <div className="wd-progress-bar">
                        <div
                          className={`wd-progress-fill ${course.pct === 100 ? 'complete' : course.pct < 25 ? 'low' : ''}`}
                          style={{ width: `${course.pct}%` }}
                        />
                      </div>
                      {course.pct === 100 && (
                        <div className="wd-badge-earned">
                          <span className="wd-badge-icon">🏅</span>
                          <div>
                            <p className="wd-badge-title">{t.badgeEarned}</p>
                            <p className="wd-badge-sub">{course.title}</p>
                          </div>
                        </div>
                      )}
                      <button className="wd-btn primary" style={{ marginTop: '12px', width: '100%' }} onClick={() => setActiveCourse(course)}>
                        {course.pct === 0 ? t.startCourse : course.pct === 100 ? t.reviewCourse : t.continueCourse}
                      </button>
                    </div>
                  ))}
                </div>
              )}


              {/* Badges summary */}
              {earnedBadges.length > 0 && (
                <div className="wd-badges-summary">
                  <h3>{t.earnedBadges}</h3>
                  <div className="wd-badges-row">
                    {earnedBadges.map(c => (
                      <div key={c.id} className="wd-badge-chip">
                        <span>🏅</span> {c.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── FEED tab ─────────────────────────────────────── */}
          {activeTab === 'feed' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>{t.activityFeedTitle}</h2>
                <p>{t.recentActivity}</p>
              </div>

              {feedItems.length === 0 ? (
                <div className="wd-empty-state">
                  <span>📰</span>
                  <p>{t.noActivityYet}</p>
                </div>
              ) : (
                <div className="wd-feed-list">
                  {feedItems.map((item, i) => (
                    <div key={i} className={`wd-feed-item ${item.type}`}>
                      <div className="wd-feed-icon">
                        {item.type === 'update' ? '🔄' : '💬'}
                      </div>
                      <div className="wd-feed-body">
                        <div className="wd-feed-header">
                          <span className="wd-feed-proj">{item.proj}</span>
                          <span className={`wd-feed-type-badge ${item.type}`}>
                            {item.type === 'update' ? t.progressUpdate : t.comment}
                          </span>
                        </div>
                        <p className="wd-feed-text">{item.text}</p>
                        <div className="wd-feed-meta">
                          <span>{item.by}</span>
                          {item.type === 'update' && <span className="wd-feed-pct">{item.pct}%</span>}
                          {item.role && <span className={`wd-comment-role ${item.role}`}>{item.role}</span>}
                          <span>{new Date(item.at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── IT SUPPORT tab ──────────────────────────────── */}
          {activeTab === 'it-support' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>{t.itSupportTitle}</h2>
                <p>{t.itSupportDesc}</p>
              </div>
              <div className="wd-it-chat">
                <div className="wd-it-messages">
                  {itMessages.length === 0 && (
                    <div className="wd-it-welcome">
                      <span>💬</span>
                      <p>{lang === 'fr' ? 'Aucun message. Envoyez votre première demande.' : 'No messages yet. Send your first request.'}</p>
                    </div>
                  )}
                  {itMessages.map(m => (
                    <div key={m.id} className={`wd-it-bubble ${m.sender}`}>
                      <span className="wd-it-bubble-name">
                        {m.sender === 'worker' ? session.name : (m.agentName || 'IT Team')}
                      </span>
                      <p className="wd-it-bubble-text">{m.text}</p>
                      <span className="wd-it-bubble-time">{new Date(m.timestamp).toLocaleString()}</span>
                    </div>
                  ))}
                  <div ref={itEndRef} />
                </div>
                <form className="wd-it-form" onSubmit={handleItSend}>
                  <textarea
                    className="wd-comment-input"
                    value={itInput}
                    onChange={e => setItInput(e.target.value)}
                    placeholder={t.itSupportPlaceholder}
                    rows={3}
                  />
                  <button type="submit" className="wd-btn primary" disabled={!itInput.trim()}>
                    {t.itSupportSend}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* ── CUSTOMER CHAT tab (workers with chat_access) ─── */}
          {activeTab === 'customer-chat' && session?.chat_access && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>{lang === 'fr' ? 'Chat Clients' : 'Customer Chat'}</h2>
                <p>{lang === 'fr' ? 'Répondez aux visiteurs en temps réel.' : 'Respond to customer inquiries in real-time.'}</p>
              </div>
              <div style={{ display: 'flex', gap: 0, height: 520, background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                {/* Session list */}
                <div style={{ width: 220, borderRight: '1px solid #e5e7eb', overflowY: 'auto', background: '#f9fafb' }}>
                  {custSessions.length === 0 ? (
                    <div style={{ padding: 20, color: '#888', fontSize: '0.85rem' }}>{lang === 'fr' ? 'Aucune conversation.' : 'No conversations yet.'}</div>
                  ) : custSessions.map(s => (
                    <div
                      key={s.sessionId}
                      onClick={() => setCustActiveSession(s.sessionId)}
                      style={{ padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid #e5e7eb', background: custActiveSession === s.sessionId ? '#eff6ff' : 'transparent', borderLeft: custActiveSession === s.sessionId ? '3px solid #2563eb' : '3px solid transparent' }}
                    >
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>{s.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.lastMessage}</div>
                      {s.lastSender === 'visitor' && <span style={{ display: 'inline-block', marginTop: 4, background: '#ef4444', color: '#fff', borderRadius: 99, padding: '1px 7px', fontSize: '0.7rem' }}>New</span>}
                    </div>
                  ))}
                </div>
                {/* Messages pane */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {!custActiveSession ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
                      {lang === 'fr' ? 'Sélectionnez une conversation.' : 'Select a conversation to reply.'}
                    </div>
                  ) : (
                    <>
                      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {custSessionMsgs.map((m, i) => {
                          const isAgent = m.sender === 'admin'
                          return (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isAgent ? 'flex-end' : 'flex-start' }}>
                              <div style={{ maxWidth: '72%', padding: '8px 12px', borderRadius: 10, background: isAgent ? '#2563eb' : '#f1f5f9', color: isAgent ? '#fff' : '#1e293b', fontSize: '0.875rem' }}>
                                {m.text}
                              </div>
                              <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>
                                {isAgent ? (m.name || session.name) : 'Visitor'} · {new Date(m.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )
                        })}
                        <div ref={custEndRef} />
                      </div>
                      <form onSubmit={handleCustSendReply} style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          value={custReply}
                          onChange={e => setCustReply(e.target.value)}
                          placeholder={lang === 'fr' ? 'Tapez votre réponse…' : 'Type your reply…'}
                          style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', outline: 'none' }}
                        />
                        <button type="submit" style={{ padding: '8px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                          {lang === 'fr' ? 'Envoyer' : 'Send'}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── TEAM DIRECTORY tab ───────────────────────────── */}
          {activeTab === 'team-directory' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>{t.teamDirTitle}</h2>
                <p>{t.teamDirDesc}</p>
              </div>
              <div className="wd-team-grid">
                {workers.filter(w => w.status === 'active').map(w => (
                  <div key={w.id} className="wd-team-card">
                    <div className="wd-team-avatar">
                      {w.avatar ? <img src={w.avatar} alt={w.name} /> : <span>{getInitials(w.name)}</span>}
                    </div>
                    <div className="wd-team-info">
                      <h4 className="wd-team-name">{w.name}</h4>
                      <p className="wd-team-role">{w.role}</p>
                      <p className="wd-team-dept">{w.department?.replace(/_/g, ' ')}</p>
                      {w.email && <a className="wd-team-email" href={`mailto:${w.email}`}>{w.email}</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── MY SCHEDULE / SHIFT CALENDAR ─────────────────── */}
          {(activeTab === 'my-schedule' || activeTab === 'shift-calendar') && (() => {
            const today = new Date()
            const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate()
            const firstDay = new Date(calYear, calMonth, 1).getDay()
            const weekdays = lang === 'fr' ? WEEKDAYS_FR : WEEKDAYS_EN
            const months = lang === 'fr' ? MONTHS_FR : MONTHS_EN
            const offDates = new Set()
            timeOffRequests.filter(r => r.status === 'approved').forEach(r => {
              let d = new Date(r.startDate + 'T00:00:00')
              const end = new Date(r.endDate + 'T00:00:00')
              while (d <= end) { offDates.add(d.toISOString().slice(0,10)); d.setDate(d.getDate()+1) }
            })
            const cells = []
            for (let i = 0; i < firstDay; i++) cells.push(null)
            for (let d = 1; d <= daysInMonth; d++) cells.push(d)
            const isToday = (d) => calYear === today.getFullYear() && calMonth === today.getMonth() && d === today.getDate()
            const isOff = (d) => offDates.has(`${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`)
            return (
              <div className="wd-section" key="schedule">
                <div className="wd-section-header">
                  <h2>{activeTab === 'shift-calendar' ? (lang === 'fr' ? 'Calendrier des Quarts' : 'Shift Calendar') : (lang === 'fr' ? 'Mon Calendrier' : 'My Schedule')}</h2>
                  <p>{lang === 'fr' ? 'Votre planning mensuel. Les congés approuvés sont surlignés en vert.' : 'Your monthly schedule. Approved time off is highlighted in green.'}</p>
                </div>
                <div className="wd-calendar">
                  <div className="wd-cal-nav">
                    <button className="wd-btn secondary small" onClick={() => { let m = calMonth-1, y = calYear; if (m<0){m=11;y--} setCalMonth(m);setCalYear(y) }}>←</button>
                    <h3 className="wd-cal-title">{months[calMonth]} {calYear}</h3>
                    <button className="wd-btn secondary small" onClick={() => { let m = calMonth+1, y = calYear; if (m>11){m=0;y++} setCalMonth(m);setCalYear(y) }}>→</button>
                    <button className="wd-btn secondary small" onClick={() => { setCalMonth(today.getMonth()); setCalYear(today.getFullYear()) }} style={{marginLeft:'auto'}}>{lang==='fr'?'Aujourd\'hui':'Today'}</button>
                  </div>
                  <div className="wd-cal-weekdays">
                    {weekdays.map(w => <div key={w} className="wd-cal-weekday">{w}</div>)}
                  </div>
                  <div className="wd-cal-grid">
                    {cells.map((d, i) => (
                      <div key={i} className={`wd-cal-cell${d === null ? ' empty' : ''}${d && isToday(d) ? ' today' : ''}${d && isOff(d) ? ' time-off' : ''}`}>
                        {d && <span className="wd-cal-day">{d}</span>}
                        {d && isOff(d) && <span className="wd-cal-off-label">{lang==='fr'?'Congé':'Off'}</span>}
                      </div>
                    ))}
                  </div>
                  <div className="wd-cal-legend">
                    <div className="wd-cal-legend-item"><div className="wd-cal-legend-dot today-dot" /><span>{lang==='fr'?"Aujourd'hui":'Today'}</span></div>
                    <div className="wd-cal-legend-item"><div className="wd-cal-legend-dot off-dot" /><span>{lang==='fr'?'Congé approuvé':'Approved time off'}</span></div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* ── TIME OFF tab ──────────────────────────────────── */}
          {activeTab === 'time-off' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>{lang === 'fr' ? 'Demandes de Congé & Vacances' : 'Time Off & Vacation Requests'}</h2>
                <p>{lang === 'fr' ? 'Soumettez vos demandes. L\'admin approuvera ou refusera.' : 'Submit requests. Your admin will approve or deny them.'}</p>
              </div>
              <div className="wd-card" style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 16 }}>{lang === 'fr' ? 'Nouvelle Demande' : 'New Request'}</h4>
                <form className="wd-update-form" onSubmit={handleTimeOffSubmit}>
                  <label>
                    {lang === 'fr' ? 'Type de Congé' : 'Leave Type'}
                    <select value={timeOffForm.type} onChange={e => setTimeOffForm(f => ({...f, type: e.target.value}))}>
                      <option value="vacation">{lang==='fr'?'🏖️ Vacances':'🏖️ Vacation'}</option>
                      <option value="sick">{lang==='fr'?'🏥 Maladie':'🏥 Sick Leave'}</option>
                      <option value="personal">{lang==='fr'?'👤 Personnel':'👤 Personal Leave'}</option>
                      <option value="parental">{lang==='fr'?'👶 Congé Parental':'👶 Parental Leave'}</option>
                      <option value="bereavement">{lang==='fr'?'🕊️ Deuil':'🕊️ Bereavement'}</option>
                    </select>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <label>
                      {lang==='fr'?'Date de Début':'Start Date'} <span className="wd-required">*</span>
                      <input type="date" value={timeOffForm.startDate} onChange={e => setTimeOffForm(f => ({...f, startDate: e.target.value}))} required />
                    </label>
                    <label>
                      {lang==='fr'?'Date de Fin':'End Date'} <span className="wd-required">*</span>
                      <input type="date" value={timeOffForm.endDate} onChange={e => setTimeOffForm(f => ({...f, endDate: e.target.value}))} required min={timeOffForm.startDate} />
                    </label>
                  </div>
                  <label>
                    {lang==='fr'?'Motif (optionnel)':'Reason (optional)'}
                    <textarea value={timeOffForm.reason} onChange={e => setTimeOffForm(f => ({...f, reason: e.target.value}))} placeholder={lang==='fr'?'Décrivez brièvement...':'Brief description...'} rows={2} />
                  </label>
                  <div className="wd-form-actions">
                    <button type="submit" className="wd-btn primary" disabled={timeOffLoading}>
                      {timeOffLoading ? '...' : (lang==='fr'?'Soumettre':'Submit Request')}
                    </button>
                    {timeOffMsg && <span className="wd-status-msg">{timeOffMsg}</span>}
                  </div>
                </form>
              </div>
              <h4 style={{ marginBottom: 12 }}>{lang==='fr'?'Historique des Demandes':'Request History'}</h4>
              {timeOffRequests.length === 0 ? (
                <div className="wd-empty-state"><span>🏖️</span><p>{lang==='fr'?'Aucune demande pour l\'instant.':'No requests yet.'}</p></div>
              ) : (
                <div className="wd-timeoff-list">
                  {timeOffRequests.map(r => {
                    const icons = { vacation:'🏖️', sick:'🏥', personal:'👤', parental:'👶', bereavement:'🕊️' }
                    const labels = { vacation: lang==='fr'?'Vacances':'Vacation', sick: lang==='fr'?'Maladie':'Sick Leave', personal: lang==='fr'?'Personnel':'Personal', parental: lang==='fr'?'Parental':'Parental', bereavement: lang==='fr'?'Deuil':'Bereavement' }
                    const days = Math.round((new Date(r.endDate) - new Date(r.startDate)) / 86400000) + 1
                    return (
                      <div key={r.id} className={`wd-timeoff-item status-${r.status}`}>
                        <div className="wd-timeoff-left">
                          <span className="wd-timeoff-icon">{icons[r.type] || '📅'}</span>
                          <div>
                            <p className="wd-timeoff-type">{labels[r.type] || r.type}</p>
                            <p className="wd-timeoff-dates">{r.startDate} → {r.endDate} <span style={{color:'#888'}}>({days} {lang==='fr'?'jour(s)':'day(s)'})</span></p>
                            {r.reason && <p className="wd-timeoff-reason">"{r.reason}"</p>}
                            {r.adminNote && <p className="wd-timeoff-admin-note">📝 {r.adminNote}</p>}
                          </div>
                        </div>
                        <div className={`wd-timeoff-badge ${r.status}`}>
                          {r.status === 'pending' ? (lang==='fr'?'⏳ En Attente':'⏳ Pending') :
                           r.status === 'approved' ? (lang==='fr'?'✅ Approuvé':'✅ Approved') :
                           (lang==='fr'?'❌ Refusé':'❌ Denied')}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── SAFETY GUIDELINES tab ────────────────────────── */}
          {activeTab === 'safety-guidelines' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>🛡️ {lang==='fr'?'Consignes de Sécurité':'Safety Guidelines'}</h2>
                <p>{lang==='fr'?'Protocoles essentiels de sécurité au travail — Groupe Ouambo.':'Essential workplace safety protocols — Groupe Ouambo.'}</p>
              </div>
              <div className="wd-policy-doc">
                {lang === 'fr' ? (
                  <>
                    <div className="wd-policy-section">
                      <h3>1. Équipements de Protection Individuelle (EPI)</h3>
                      <p>Tout le personnel opérationnel est tenu de porter les EPI appropriés en tout temps sur les sites de travail :</p>
                      <ul>
                        <li>🪖 <strong>Casque de sécurité</strong> — obligatoire sur tous les chantiers et zones de chargement/déchargement</li>
                        <li>🦺 <strong>Gilet haute visibilité</strong> — obligatoire dans les cours de véhicules et entrepôts</li>
                        <li>🥾 <strong>Chaussures de sécurité</strong> — semelles anti-perforation et embouts d'acier</li>
                        <li>🧤 <strong>Gants de protection</strong> — lors de la manipulation de marchandises, matériaux ou produits chimiques</li>
                        <li>🥽 <strong>Lunettes de protection</strong> — dans les zones de poussière, soudure ou produits chimiques</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>2. Sécurité des Véhicules et du Transport</h3>
                      <ul>
                        <li>Vérification obligatoire du véhicule avant chaque départ (niveaux, freins, feux, pneus)</li>
                        <li>Ceinture de sécurité obligatoire à tout moment — conducteurs et passagers</li>
                        <li>Vitesse maximale : 80 km/h sur route nationale, 40 km/h en zone agglomérée</li>
                        <li>Interdiction formelle de conduire sous l'influence de l'alcool ou de substances</li>
                        <li>Repos obligatoire de 30 min toutes les 4 heures de conduite</li>
                        <li>Signalement immédiat de tout accident ou incident à votre superviseur</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>3. Manutention et Stockage</h3>
                      <ul>
                        <li>Charge maximale manuelle : 25 kg par personne (technique de levage dos droit)</li>
                        <li>Utiliser les équipements de levage pour toute charge supérieure à 25 kg</li>
                        <li>Les produits dangereux (carburant, produits chimiques) doivent être étiquetés et stockés séparément</li>
                        <li>Respecter les zones de circulation — ne jamais bloquer les issues de secours</li>
                        <li>Vérifier la stabilité des palettes et rayonnages avant manipulation</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>4. Procédures d'Urgence</h3>
                      <ul>
                        <li>🚨 <strong>Numéro d'urgence interne :</strong> contacter votre superviseur immédiatement</li>
                        <li>🔥 <strong>Incendie :</strong> activer l'alarme, évacuer, appeler les secours. Ne jamais utiliser l'ascenseur</li>
                        <li>⚕️ <strong>Premiers secours :</strong> localiser la trousse de premiers soins la plus proche. Les référents sécurité sont formés aux gestes de premiers secours</li>
                        <li>☣️ <strong>Déversement de produits dangereux :</strong> isoler la zone, avertir le responsable HSE immédiatement</li>
                        <li>📍 <strong>Points de rassemblement :</strong> se rendre au point de rassemblement désigné lors de toute évacuation</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>5. Signalement des Incidents</h3>
                      <p>Tout incident, accident ou quasi-accident doit être signalé dans les 24 heures via l'onglet <strong>Rapport d'Incident</strong> du portail. Les signalements confidentiels peuvent être effectués via l'onglet <strong>Alerte Éthique</strong>. La sécurité de tous dépend de la transparence de chacun.</p>
                    </div>
                    <div className="wd-policy-meta">
                      <p>Politique approuvée par la Direction Générale — Groupe Ouambo</p>
                      <p>Version en vigueur : Janvier 2025 | Prochaine révision : Janvier 2026</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="wd-policy-section">
                      <h3>1. Personal Protective Equipment (PPE)</h3>
                      <p>All operational personnel are required to wear appropriate PPE at all times on work sites:</p>
                      <ul>
                        <li>🪖 <strong>Safety helmet</strong> — mandatory on all job sites and loading/unloading areas</li>
                        <li>🦺 <strong>High-visibility vest</strong> — mandatory in vehicle yards and warehouses</li>
                        <li>🥾 <strong>Safety boots</strong> — anti-puncture soles and steel toe caps required</li>
                        <li>🧤 <strong>Protective gloves</strong> — when handling goods, materials, or chemicals</li>
                        <li>🥽 <strong>Safety goggles</strong> — in dusty zones, welding areas, or chemical handling areas</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>2. Vehicle and Transport Safety</h3>
                      <ul>
                        <li>Mandatory pre-departure vehicle check (fluids, brakes, lights, tyres)</li>
                        <li>Seatbelts mandatory at all times for drivers and passengers</li>
                        <li>Speed limits: 80 km/h on national roads, 40 km/h in urban areas</li>
                        <li>Zero tolerance for driving under the influence of alcohol or substances</li>
                        <li>Mandatory 30-minute rest every 4 hours of continuous driving</li>
                        <li>All accidents and incidents must be reported to your supervisor immediately</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>3. Manual Handling and Storage</h3>
                      <ul>
                        <li>Maximum manual handling load: 25 kg per person (straight-back lifting technique)</li>
                        <li>Use lifting equipment for any load exceeding 25 kg</li>
                        <li>Hazardous materials (fuel, chemicals) must be labeled and stored separately</li>
                        <li>Respect traffic zones — never block emergency exits</li>
                        <li>Check stability of pallets and racking before handling</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>4. Emergency Procedures</h3>
                      <ul>
                        <li>🚨 <strong>Internal emergency number:</strong> contact your supervisor immediately</li>
                        <li>🔥 <strong>Fire:</strong> activate the alarm, evacuate, call emergency services. Never use the elevator</li>
                        <li>⚕️ <strong>First aid:</strong> locate the nearest first aid kit. Safety officers are trained in first aid</li>
                        <li>☣️ <strong>Hazardous spill:</strong> isolate the area, notify the HSE manager immediately</li>
                        <li>📍 <strong>Assembly points:</strong> proceed to the designated assembly point during any evacuation</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>5. Incident Reporting</h3>
                      <p>All incidents, accidents, or near-misses must be reported within 24 hours via the <strong>Incident Report</strong> tab on this portal. Confidential reports can be submitted via the <strong>Whistleblower</strong> tab. Everyone's safety depends on everyone's transparency.</p>
                    </div>
                    <div className="wd-policy-meta">
                      <p>Policy approved by Executive Management — Groupe Ouambo</p>
                      <p>Current version: January 2025 | Next review: January 2026</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── CODE OF CONDUCT / COMPANY POLICY tab ──────────── */}
          {activeTab === 'code-of-conduct' && (
            <div className="wd-section">
              <div className="wd-section-header">
                <h2>📜 {lang==='fr'?'Code de Conduite & Politique d\'Entreprise':'Code of Conduct & Company Policy'}</h2>
                <p>{lang==='fr'?'Valeurs, standards et comportements attendus au sein du Groupe Ouambo.':'Values, standards, and expected behaviors within Groupe Ouambo.'}</p>
              </div>
              <div className="wd-policy-doc">
                {lang === 'fr' ? (
                  <>
                    <div className="wd-policy-section">
                      <h3>1. Nos Valeurs Fondamentales</h3>
                      <ul>
                        <li>🤝 <strong>Intégrité</strong> — Nous agissons avec honnêteté et transparence dans toutes nos interactions</li>
                        <li>🚀 <strong>Excellence</strong> — Nous visons le plus haut standard dans chaque tâche, grande ou petite</li>
                        <li>🌍 <strong>Responsabilité</strong> — Nous honorons nos engagements envers les clients, collègues et communautés</li>
                        <li>🤲 <strong>Solidarité</strong> — Nous nous soutenons mutuellement et valorisons la diversité de nos équipes</li>
                        <li>🌱 <strong>Durabilité</strong> — Nous agissons en gardant à l'esprit les générations futures et l'environnement</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>2. Comportements Professionnels Attendus</h3>
                      <ul>
                        <li>Traiter tous les collègues, clients et partenaires avec respect et dignité</li>
                        <li>Maintenir la confidentialité des informations sensibles de l'entreprise et des clients</li>
                        <li>Être ponctuel et prévenir en avance en cas d'absence ou de retard</li>
                        <li>Utiliser les ressources de l'entreprise (véhicules, matériel, données) uniquement à des fins professionnelles</li>
                        <li>S'abstenir de tout comportement discriminatoire, harcelant ou intimidant</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>3. Conflits d'Intérêts</h3>
                      <p>Les employés doivent déclarer tout conflit d'intérêt réel ou potentiel à leur superviseur. Il est interdit de :</p>
                      <ul>
                        <li>Exercer une activité concurrente sans autorisation écrite préalable</li>
                        <li>Accepter des cadeaux d'une valeur supérieure à 25 000 FCFA de clients ou fournisseurs</li>
                        <li>Utiliser des informations internes à des fins personnelles ou commerciales extérieures</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>4. Usage des Technologies</h3>
                      <ul>
                        <li>Les systèmes informatiques et communications de l'entreprise sont réservés à un usage professionnel</li>
                        <li>Interdiction de télécharger ou diffuser des contenus illicites, offensants ou protégés par droits d'auteur</li>
                        <li>Les mots de passe doivent être gardés confidentiels et changés tous les 90 jours</li>
                        <li>Signaler immédiatement toute compromission de sécurité informatique à l'équipe IT</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>5. Politique de Congés et Présence</h3>
                      <ul>
                        <li><strong>Congés annuels :</strong> 18 jours ouvrables par an pour les employés avec moins de 5 ans d'ancienneté ; 24 jours au-delà</li>
                        <li><strong>Congé maladie :</strong> jusqu'à 15 jours par an sur présentation d'un certificat médical</li>
                        <li><strong>Congé maternité/paternité :</strong> 14 semaines pour les mères ; 5 jours pour les pères</li>
                        <li>Les demandes de congé doivent être soumises au minimum 5 jours ouvrables à l'avance via le portail</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>6. Sanctions Disciplinaires</h3>
                      <p>Tout manquement à ce code de conduite peut entraîner, selon la gravité : un avertissement écrit, une mise à pied, une rétrogradation ou un licenciement. Les infractions graves (fraude, violence, harcèlement) entraînent une procédure de licenciement immédiat.</p>
                    </div>
                    <div className="wd-policy-meta">
                      <p>Politique approuvée par la Direction Générale — Groupe Ouambo</p>
                      <p>Version en vigueur : Janvier 2025 | Prochaine révision : Janvier 2026</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="wd-policy-section">
                      <h3>1. Our Core Values</h3>
                      <ul>
                        <li>🤝 <strong>Integrity</strong> — We act with honesty and transparency in all interactions</li>
                        <li>🚀 <strong>Excellence</strong> — We pursue the highest standard in every task, large or small</li>
                        <li>🌍 <strong>Accountability</strong> — We honor our commitments to clients, colleagues, and communities</li>
                        <li>🤲 <strong>Solidarity</strong> — We support one another and value the diversity of our teams</li>
                        <li>🌱 <strong>Sustainability</strong> — We act with future generations and the environment in mind</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>2. Expected Professional Behaviors</h3>
                      <ul>
                        <li>Treat all colleagues, clients, and partners with respect and dignity</li>
                        <li>Maintain confidentiality of sensitive company and client information</li>
                        <li>Be punctual and notify in advance if you will be absent or late</li>
                        <li>Use company resources (vehicles, equipment, data) only for professional purposes</li>
                        <li>Refrain from any discriminatory, harassing, or intimidating behavior</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>3. Conflicts of Interest</h3>
                      <p>Employees must disclose any actual or potential conflicts of interest to their supervisor. It is prohibited to:</p>
                      <ul>
                        <li>Engage in competing activities without prior written authorization</li>
                        <li>Accept gifts valued above 25,000 FCFA from clients or suppliers</li>
                        <li>Use internal information for personal or external commercial purposes</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>4. Technology Use</h3>
                      <ul>
                        <li>Company IT systems and communications are reserved for professional use</li>
                        <li>Prohibited from downloading or distributing illegal, offensive, or copyrighted content</li>
                        <li>Passwords must be kept confidential and changed every 90 days</li>
                        <li>Report any IT security breach immediately to the IT team</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>5. Leave and Attendance Policy</h3>
                      <ul>
                        <li><strong>Annual leave:</strong> 18 working days per year for employees with less than 5 years tenure; 24 days beyond that</li>
                        <li><strong>Sick leave:</strong> up to 15 days per year with a medical certificate</li>
                        <li><strong>Maternity/paternity leave:</strong> 14 weeks for mothers; 5 days for fathers</li>
                        <li>Leave requests must be submitted at least 5 working days in advance via the portal</li>
                      </ul>
                    </div>
                    <div className="wd-policy-section">
                      <h3>6. Disciplinary Sanctions</h3>
                      <p>Any breach of this code of conduct may result, depending on severity, in: a written warning, suspension, demotion, or termination. Serious offenses (fraud, violence, harassment) trigger an immediate termination procedure.</p>
                    </div>
                    <div className="wd-policy-meta">
                      <p>Policy approved by Executive Management — Groupe Ouambo</p>
                      <p>Current version: January 2025 | Next review: January 2026</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── PLACEHOLDER tabs (all sidebar links) ─────────── */}
          {canManageBilling && (activeTab === 'quotes' || activeTab === 'invoices') && (
            <WorkerBilling mode={activeTab} lang={lang} />
          )}

          {PLACEHOLDER_PAGES[activeTab] && (
            <div className="wd-section">
              <div className="wd-placeholder-page">
                <div className="wd-placeholder-icon">{PLACEHOLDER_PAGES[activeTab].icon}</div>
                <h2 className="wd-placeholder-title">{PLACEHOLDER_PAGES[activeTab][lang]}</h2>
                <p className="wd-placeholder-desc">{PLACEHOLDER_PAGES[activeTab][`desc_${lang}`]}</p>
                <div className="wd-placeholder-coming">
                  <span>🚧</span>
                  <p>{t.comingSoon}</p>
                  <p className="wd-placeholder-coming-sub">{t.comingSoonDesc}</p>
                </div>
              </div>
            </div>
          )}

          {/* ── PROFILE tab ──────────────────────────────────── */}
          {activeTab === 'profile' && (
            <div className="wd-section">
              <div className="wd-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h2>{t.myProfile}</h2>
                  <p>{t.updateProfileDesc}</p>
                </div>
                <button className="wd-logout-btn-profile" onClick={handleLogout}>{t.signOut}</button>
              </div>

              <div className="wd-profile-grid">
                <div className="wd-card wd-profile-avatar-card">
                  <div className="wd-profile-avatar-wrap">
                    {(myWorker?.avatar || session.avatar)
                      ? <img src={myWorker?.avatar || session.avatar} alt="avatar" className="wd-profile-avatar-img" />
                      : <div className="wd-profile-avatar-placeholder">{getInitials(session.name)}</div>
                    }
                    <button className="wd-avatar-change-btn" onClick={() => avatarInputRef.current?.click()}>
                      {t.changePhoto}
                    </button>
                    <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
                  </div>
                  <div className="wd-profile-info">
                    <h3>{session.name}</h3>
                    <p>{session.department?.replace(/_/g, ' ')}</p>
                    {myWorker?.region && <p>{myWorker.region}</p>}
                    {myWorker?.email  && <p>{myWorker.email}</p>}
                    {myWorker?.phone  && <p>{myWorker.phone}</p>}
                  </div>
                  {earnedBadges.length > 0 && (
                    <div className="wd-profile-badges">
                      <h4>{t.earnedBadges}</h4>
                      <div className="wd-badges-row">
                        {earnedBadges.map(c => (
                          <div key={c.id} className="wd-badge-chip">
                            <span>🏅</span> {c.title}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="wd-card">
                  <h4>{t.changePassword}</h4>
                  <form className="wd-update-form" onSubmit={handleChangePassword}>
                    <label>
                      {t.newPassword} <span className="wd-required">*</span>
                      <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder={t.enterNewPassword} required />
                    </label>
                    <label>
                      {t.confirmPassword} <span className="wd-required">*</span>
                      <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder={t.reenterNewPassword} required />
                    </label>
                    <div className="wd-form-actions">
                      <button type="submit" className="wd-btn primary">{t.updatePassword}</button>
                      {profileMsg && <span className="wd-status-msg">{profileMsg}</span>}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
